import React, { useEffect, useMemo, useState } from "react";
import Conclusion from "./Conclusion";

interface InterpretationPanelProps {
  findings: string[];
  patient: { age: string; sex: string };
  patientNote: string;
  clinicianOpinion: string;
  context: any[];
  onFindingsChange?: (next: string[]) => void;
}

const InterpretationPanel: React.FC<InterpretationPanelProps> = ({
  findings,
  patient,
  patientNote,
  clinicianOpinion,
  context,
  onFindingsChange,
}) => {
  const [conclusionText, setConclusionText] = useState<string | null>(null);
  const [findingsText, setFindingsText] = useState<string>((findings ?? []).join("\n"));

  useEffect(() => {
    setFindingsText((findings ?? []).join("\n"));
  }, [findings]);

  const parsedFindings: string[] = useMemo(
    () =>
      findingsText
        .split(/\r?\n/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0),
    [findingsText]
  );

  useEffect(() => {
    onFindingsChange?.(parsedFindings);
  }, [parsedFindings, onFindingsChange]);

  return (
    <section className="surface-card interpretation-panel">
      <div className="card-header">
        <h2 className="card-title">Report</h2>
      </div>

      <section className="interpretation-section">
        <div className="section-header">
          <h3 className="section-title">Summary</h3>
        </div>
        <textarea
          value={findingsText}
          onChange={(event) => setFindingsText(event.target.value)}
          placeholder={"1. Motor NCS ...\n2. Sensory NCS ...\n3. EMG ..."}
          rows={Math.max(10, findingsText.split(/\r?\n/).length)}
          spellCheck={false}
          className="app-textarea"
        />
      </section>

      <section className="interpretation-section">
        <div className="section-header">
          <h3 className="section-title">Diagnostic Interpretation</h3>
        </div>

        <div className="interpretation-output">
          {conclusionText ? (
            conclusionText
          ) : (
            <span className="empty-state">
              Generate a diagnostic interpretation to review the final impression and comments here.
            </span>
          )}
        </div>
      </section>

      <div style={{ marginTop: "18px" }}>
        <Conclusion
          findings={parsedFindings}
          patient={patient}
          patientNote={patientNote}
          clinicianOpinion={clinicianOpinion}
          context={context}
          onResult={(result) => {
            if (result === null) {
              setConclusionText(null);
              return;
            }

            const impressions = result.conclusion?.impression ?? [];
            const comments = result.conclusion?.comments ?? [];
            const formattedConclusion = impressions
              .map((item: any, index: number) =>
                typeof item === "string"
                  ? `${index + 1}. ${item}`
                  : `${index + 1}. ${item.confidence} ${item.diagnosis}`
              )
              .join("\n");
            const formattedComments = comments.map((comment: string) => `- ${comment}`).join("\n");
            const fullText =
              formattedConclusion || formattedComments
                ? `${formattedConclusion || ""}\n\nComments:\n${formattedComments || ""}`
                : "";

            setConclusionText(fullText);
          }}
        />
      </div>
    </section>
  );
};

export default InterpretationPanel;
